import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, Link } from 'lucide-react';
import { checkUserExists } from './utils/userService';
// import { UserCheckResult } from './types/user';
import { UserExistsCard } from './components/UserExistsCard';
import { UserNotFoundCard } from './components/UserNotFoundCard';
import { TableNotFoundCard } from './components/TableNotFoundCard';
import { LoadingCard } from './components/LoadingCard';
import { ErrorCard } from './components/ErrorCard';

function UnveilPreview({ message, dataType, result, setResult, showSearch, handleStartUp, portals }) {
  const [userInput, setUserInput] = useState('');
  // const [_result, setResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!userInput.trim()) return;

    setIsSearching(true);
    setResult({ status: 'loading' });

    try {
      const searchResult = await checkUserExists(portals, userInput.trim());
      setResult(searchResult);
    } catch (error) {
      setResult({
        status: 'error',
        message: 'An unexpected error occurred. Please try again: ' + error
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleRetry = () => {
    if (dataType === "user") {
      if (userInput.trim()) {
        handleSearch();
      }
    } else {
      handleStartUp();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Demo users for easy testing
  const demoUsers = ['johndoe', 'janesmith', 'mikej'];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-gray-700">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full blur-3xl"></div>
        </div>
      </div>
      <div className="relative container mx-auto px-4 py-8">

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-12 mt-24"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6"
            >
              <User className="w-8 h-8 text-white" />
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              User Lookup Tool
            </h1>

            {showSearch && <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Enter a username, email, or user ID to check if a user exists in our system
            </p>}
          </motion.div>

          {/* Search Section */}
          {showSearch && <motion.div
            variants={itemVariants}
            className="max-w-2xl mx-auto mb-12"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Link className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter username, email, or user ID..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    disabled={isSearching}
                  />
                </div>

                <motion.button
                  onClick={handleSearch}
                  disabled={isSearching || !userInput.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                >
                  <Search className="w-5 h-5 mr-2" />
                  {isSearching ? 'Searching...' : 'Search'}
                </motion.button>
              </div>

              {/* Demo Users */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-4 pt-4 border-t border-gray-200"
              >
                <p className="text-sm text-gray-500 mb-3">Try these demo users:</p>
                <div className="flex flex-wrap gap-2">
                  {demoUsers.map((user) => (
                    <motion.button
                      key={user}
                      onClick={() => setUserInput(user)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors duration-200"
                    >
                      {user}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>}

          {/* Results Section */}
          <AnimatePresence mode="wait">
            {result && (
              <motion.div
                key={result.status}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                {result.status === 'loading' && <LoadingCard message={result.message}/>}

                {result.status === 'exists' && (
                  <UserExistsCard user={result.user} />
                )}

                {result.status === 'not_found' && (
                  <UserNotFoundCard message={result.message}  onRetry={handleRetry}/>
                )}

                {result.status === 'table_not_found' && (
                  <TableNotFoundCard message={result.message} />
                )}

                {result.status === 'error' && (
                  <ErrorCard message={result.message} onRetry={handleRetry} />
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <motion.footer
            variants={itemVariants}
            className="text-center text-gray-500 text-sm mt-16"
          >
            <p>tso ministries: till every eye sees Jesus</p>
          </motion.footer>
        </motion.div>
      </div>
    </div>
  );
}

export default UnveilPreview;